import {
  Button,
  MessageBar,
  MessageBarBody,
  Spinner,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { ArrowClockwise16Regular, Play16Regular, Stop16Regular } from '@fluentui/react-icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type { Device, DeviceStatus, MetricsRange, ThroughputResult, TopTalker } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import { useMe } from '../auth/MeProvider';
import { ActivityTable } from '../components/ActivityTable';
import { NoAccess } from '../components/NoAccess';
import { PresenceDot } from '../components/PresenceDot';
import { PageHeader } from '../components/PageHeader';
import { RelayPanel } from '../components/RelayPanel';
import { Sparkline } from '../components/Sparkline';
import { StatCard } from '../components/StatCard';
import { env } from '../env';
import { formatBytes, formatMbit } from '../lib/format';
import { useShell } from '../lib/shell';
import { useAudit } from '../lib/useAudit';
import { errorText } from '../lib/useRelay';
import { vt } from '../theme';
import { relayMbit } from './map/model';
import {
  OVERVIEW_RANGES,
  ago,
  autoStop,
  autoStopHint,
  costView,
  minutesLeft,
  onlineText,
  orderDevices,
  stateWord,
  uptime,
} from './overview/model';
import { BarList } from './traffic/BarList';
import { StackedChart } from './traffic/StackedChart';
import { colorOf, toBands, type Stacked } from './traffic/model';

/** The mockup draws the five newest events; "Full log" opens the whole trail at /activity. */
const AUDIT_ROWS = 5;

const useStyles = makeStyles({
  tabs: { display: 'flex', columnGap: '4px' },
  tab: {
    height: '28px',
    lineHeight: '28px',
    paddingInline: '12px',
    borderRadius: tokens.borderRadiusCircular,
    border: 'none',
    backgroundColor: 'transparent',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    color: vt.fg3,
    cursor: 'pointer',
    ':hover': { color: vt.fg1 },
    ':focus-visible': { outline: `2px solid ${vt.brand}`, outlineOffset: '1px' },
  },
  tabSelected: { backgroundColor: vt.tintNeutral, color: vt.fg2, fontWeight: tokens.fontWeightSemibold },
  // Mockup 01's spacing between the strips: tighter than other pages, so the throughput band fits at 1440×900.
  body: { display: 'flex', flexDirection: 'column', rowGap: '14px', flexShrink: 0 },
  stats: { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', columnGap: '14px' },
  middle: { display: 'grid', gridTemplateColumns: '56fr 44fr', columnGap: '20px', alignItems: 'start' },
  sectionHead: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', height: '28px' },
  sectionTitle: { margin: 0, fontSize: '17px', fontWeight: tokens.fontWeightSemibold, color: vt.fg1 },
  viewAll: {
    color: vt.brand,
    fontSize: '11.5px',
    textDecorationLine: 'none',
    ':hover': { textDecorationLine: 'underline' },
  },
  hint: { fontSize: '11.5px', color: vt.fg3, fontWeight: tokens.fontWeightRegular, marginLeft: '16px' },
  devList: { listStyleType: 'none', margin: 0, padding: 0, borderTop: `1px solid ${vt.surface2}` },
  devRow: {
    display: 'grid',
    gridTemplateColumns: '14px minmax(0, 160px) 100px 52px',
    columnGap: '6px',
    alignItems: 'center',
    height: '32px',
    borderBottom: `1px solid ${vt.surface2}`,
    ':last-child': { borderBottomStyle: 'none' },
  },
  devDot: { justifySelf: 'center' },
  devName: { color: vt.fg1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: '11.5px', color: vt.fg2 },
  seen: { textAlign: 'end' },
  // An offline device reads as greyed out, below the ones that are up.
  offline: { color: vt.fg4 },
  empty: { color: vt.fg3, paddingBlock: '12px' },
  live: { display: 'grid', gridTemplateColumns: '56fr 44fr', columnGap: '30px', alignItems: 'start' },
  livePanel: { display: 'flex', flexDirection: 'column', rowGap: '6px', minWidth: 0 },
  cached: { color: vt.orange },
  unavailable: { color: vt.fg3, cursor: 'help' },
});

/** Re-render every `ms` so the uptime and the idle countdown keep moving. */
function useNow(ms: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(id);
  }, [ms]);
  return now;
}

function DeviceList({
  devices,
  statuses,
  live,
  error,
  now,
}: {
  devices: Device[] | null;
  statuses: DeviceStatus[] | null;
  live: ReadonlySet<string>;
  error: string | null;
  now: number;
}) {
  const s = useStyles();
  return (
    <section aria-label="Devices">
      <div className={s.sectionHead}>
        <h2 className={s.sectionTitle}>Devices</h2>
        <RouterLink to="/devices" className={s.viewAll}>
          View all
        </RouterLink>
      </div>
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
      {!devices && !error && <Spinner size="tiny" label="Loading devices" />}
      {devices && (
        <ul className={s.devList}>
          {devices.length === 0 && (
            <li className={s.empty}>No devices yet. Install the VIPPER VPN client and sign in to enrol one.</li>
          )}
          {orderDevices(devices, statuses, live, now).map(({ device: d, status, presence }) => {
            const off = presence === 'offline';
            return (
              <li key={d.id} className={s.devRow} data-device={d.name} data-muted={off || undefined}>
                <PresenceDot className={s.devDot} state={presence} />
                <span className={mergeClasses(s.devName, off && s.offline)}>{d.name}</span>
                <span className={mergeClasses(s.mono, off && s.offline)}>{d.overlayIp}</span>
                <span className={mergeClasses(s.mono, s.seen, off && s.offline)} title="Last handshake">
                  {statuses ? ago(status?.lastHandshakeAt ?? null, now) : '–'}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

interface Metrics {
  /** GET /metrics/throughput?by=device for the selected range, as chart bands. */
  chart: Stacked | null;
  talkers: TopTalker[] | null;
  /** GET /metrics/throughput?range=1h&by=total: the Throughput card. */
  total: ThroughputResult | null;
  error: string | null;
}

/**
 * The live-throughput band and the Throughput card (admin-only endpoints). Loaded on open, on
 * Refresh and on a range change; nothing polls, since every call wakes the scale-to-zero API.
 */
function useMetrics(enabled: boolean, range: MetricsRange) {
  const { api } = useAuth();
  const [state, setState] = useState<Metrics>({ chart: null, talkers: null, total: null, error: null });
  const seq = useRef(0);

  const loadRange = useCallback(async () => {
    const mine = ++seq.current;
    const [t, k] = await Promise.allSettled([api.throughput(range, 'device'), api.topTalkers(range, 5)]);
    // A newer range was picked while this one loaded.
    if (mine !== seq.current) return;
    const failed = [t, k].find((r) => r.status === 'rejected');
    setState((st) => ({
      ...st,
      chart: t.status === 'fulfilled' ? toBands(t.value) : null,
      talkers: k.status === 'fulfilled' ? k.value.talkers : null,
      error: failed ? errorText(failed.reason) : null,
    }));
  }, [api, range]);

  const loadTotal = useCallback(async () => {
    try {
      const total = await api.throughput('1h', 'total');
      setState((st) => ({ ...st, total }));
    } catch {
      // The card falls back to a dash; the band shows the API's error.
      setState((st) => ({ ...st, total: null }));
    }
  }, [api]);

  useEffect(() => {
    if (enabled) void loadRange();
  }, [enabled, loadRange]);
  useEffect(() => {
    if (enabled) void loadTotal();
  }, [enabled, loadTotal]);

  const reload = useCallback(() => {
    void loadRange();
    void loadTotal();
  }, [loadRange, loadTotal]);
  return { ...state, reload };
}

/** Mockup 01's last band: a preview of the Traffic screen over the selected range. */
function LiveThroughput({ range, metrics }: { range: MetricsRange; metrics: Metrics }) {
  const s = useStyles();
  const hint = OVERVIEW_RANGES.find((r) => r.id === range)?.hint ?? range;
  const { chart, talkers, error } = metrics;
  return (
    <section className={s.live} aria-label="Live throughput">
      <div className={s.livePanel}>
        <h2 className={s.sectionTitle}>
          Throughput
          <span className={s.hint}>{hint}</span>
        </h2>
        {error && (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}
        {!chart && !error && <Spinner size="tiny" label="Loading throughput" />}
        {chart && <StackedChart chart={chart} range={range} height={96} legend={false} />}
      </div>
      <div className={s.livePanel}>
        <h2 className={s.sectionTitle}>
          Top talkers
          <span className={s.hint}>{hint}</span>
        </h2>
        {talkers && (
          <BarList
            label="Top talkers"
            empty="No traffic in this range"
            items={talkers.map((t) => ({
              key: t.deviceId,
              label: t.name,
              value: t.total,
              text: formatBytes(t.total),
              color: colorOf(chart, t.deviceId),
            }))}
          />
        )}
      </div>
    </section>
  );
}

/** The screen you land on (mockup 01): relay state and cost, devices, activity, throughput. */
export function Overview() {
  const s = useStyles();
  const { me, loading, error, canUseVpn, isAdmin } = useMe();
  const shell = useShell();
  const { relay: relayState, devices, devicesError, reloadDevices, statuses, reloadStatuses } = shell;
  const audit = useAudit(isAdmin, AUDIT_ROWS);
  const [range, setRange] = useState<MetricsRange>('1h');
  const metrics = useMetrics(isAdmin, range);
  const now = useNow(1000);
  const idleMinutes = env.relayIdleMinutes;
  const { relay, pending } = relayState;

  const refresh = () => {
    void relayState.refresh();
    void reloadDevices();
    void reloadStatuses();
    if (isAdmin) {
      void shell.reloadLive();
      void audit.reload();
      void shell.reloadCost();
      metrics.reload();
    }
  };

  const deadline = autoStop(relay, idleMinutes);
  let relaySub: string | undefined;
  if (relay?.state === 'running') {
    relaySub = [uptime(relay, now), deadline !== null && `idle stop in ${minutesLeft(deadline.at, now)} min`]
      .filter(Boolean)
      .join(' · ');
  } else if (relay) {
    relaySub = `since ${ago(relay.since, now)}`;
  }
  const relayDot = relay?.state === 'running' ? vt.green : relay?.state === 'stopped' ? vt.fg4 : relay ? vt.cyan : undefined;

  const cost = costView(shell.cost, shell.costError, isAdmin);
  const mbit = relayMbit(metrics.total, now);
  const hour = metrics.total ? toBands(metrics.total) : null;
  const hourPeak = hour?.peak ?? null;
  const hourTotals = hour ? Array.from({ length: hour.points }, (_, i) => hour.bands.reduce((a, b) => a + b.mbit[i]!, 0)) : [];
  const adminOnly = (what: string) => (
    <Tooltip content={`Only admins can read ${what}.`} relationship="description">
      <span className={s.unavailable}>—</span>
    </Tooltip>
  );

  const relayButton =
    relay?.state === 'stopped' ? (
      <Button
        appearance="primary"
        shape="circular"
        size="small"
        icon={pending === 'start' ? <Spinner size="tiny" /> : <Play16Regular />}
        disabled={pending !== null}
        onClick={() => void relayState.start()}
      >
        Start relay
      </Button>
    ) : (
      <Button
        appearance="outline"
        shape="circular"
        size="small"
        icon={pending === 'stop' ? <Spinner size="tiny" /> : <Stop16Regular />}
        disabled={relay?.state !== 'running' || pending !== null}
        onClick={() => void relayState.stop()}
      >
        Stop relay
      </Button>
    );

  return (
    <>
      <PageHeader
        title="Overview"
        actions={
          me &&
          canUseVpn && (
            <>
              <Tooltip content="Refresh" relationship="label">
                <Button appearance="outline" shape="circular" size="small" icon={<ArrowClockwise16Regular />} onClick={refresh} />
              </Tooltip>
              {relayButton}
            </>
          )
        }
      >
        {isAdmin && (
          <div className={s.tabs} role="tablist" aria-label="Time range">
            {OVERVIEW_RANGES.map((r) => (
              <button
                key={r.id}
                type="button"
                role="tab"
                aria-selected={range === r.id}
                className={mergeClasses(s.tab, range === r.id && s.tabSelected)}
                onClick={() => setRange(r.id)}
              >
                {r.label}
              </button>
            ))}
          </div>
        )}
      </PageHeader>

      {loading && <Spinner label="Loading your permissions" />}
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
      {me && !canUseVpn && <NoAccess />}
      {me && canUseVpn && (
        <div className={s.body}>
          <div className={s.stats}>
            <StatCard
              label="Relay"
              value={relay ? stateWord(relay.state) : '–'}
              sub={
                deadline !== null ? (
                  <Tooltip content={autoStopHint(deadline, idleMinutes)} relationship="description">
                    <span>{relaySub}</span>
                  </Tooltip>
                ) : (
                  relaySub
                )
              }
              dot={relayDot}
            />
            <StatCard
              label="Devices online"
              value={onlineText(devices, statuses, shell.live, now)}
              sub={devices ? `${devices.length} enrolled` : undefined}
            />
            <StatCard
              label="Throughput"
              mono
              value={isAdmin ? (mbit === null ? '–' : formatMbit(mbit)) : adminOnly('throughput')}
              sub={hourPeak ? `peak ${formatMbit(hourPeak.mbit)} · last hour` : 'last hour'}
              right={isAdmin && hourTotals.length > 1 ? <Sparkline values={hourTotals} /> : undefined}
            />
            <StatCard
              label="Cost this month"
              mono
              value={
                cost.tooltip ? (
                  <Tooltip content={cost.tooltip} relationship="description">
                    <span className={cost.value === '—' ? s.unavailable : undefined}>{cost.value}</span>
                  </Tooltip>
                ) : (
                  cost.value
                )
              }
              sub={
                <>
                  relay disk + compute
                  {cost.cached && <span className={s.cached}> · cached</span>}
                </>
              }
            />
          </div>

          <div className={s.middle}>
            <RelayPanel relayState={relayState} idleMinutes={idleMinutes} now={now} />
            <DeviceList devices={devices} statuses={statuses} live={shell.live} error={devicesError} now={now} />
          </div>

          {isAdmin && (
            <ActivityTable
              events={audit.events}
              loading={audit.loading}
              error={audit.error}
              fullLogTo="/activity"
            />
          )}

          {isAdmin && <LiveThroughput range={range} metrics={metrics} />}
        </div>
      )}
    </>
  );
}
